"use strict";
/**
 * Classes that model RNG patterns.
 * @author Louis-Dominique Dubeau
 * @license MPL 2.0
 * @copyright Mangalam Research Center for Buddhist Languages
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const util = tslib_1.__importStar(require("../util"));
// XML validation against a schema could work without any lookahead if it were
// not for namespaces. However, namespace support means that the interpretation
// of a tag or of an attribute may depend on information which appears *later*
// than the earliest time at which a validation decision might be called for:
//
// Consider:
//    <elephant a="a" b="b"... xmlns="elephant_uri"/>
//
// It is not until xmlns is encountered that the validator will know that
// elephant belongs to the elephant_uri namespace. This is not too troubling for
// a validator that can access the whole document but for validators used in a
// line-by-line process (which is the case if the validator is driven by a
// CodeMirror or Ace tokenizer, and anything based on them), this can be
// problematic because the attributes could appear on lines other than the line
// on which the start of the tag appears:
//
// <elephant
//  a="a"
//  b="b"
//  xmlns="elephant_uri"/>
//
// The validator encounters the start of the tag and the attributes, without
// knowing that eventually this elephant tag belongs to the elephant_uri
// namespace. This discovery might result in things that were seen previously
// and deemed valid becoming invalid. Or things that were invalid becoming
// valid.
//
// Handling namespaces will require lookahead. Although the validator would
// still expect all events that have tag and attribute names to have a proper
// namespace uri, upon ``enterStartTag`` the parsing code which feeds events to
// the validator would look ahead for these cases:
//
// * There is a valid ``>`` character ending the start tag. Scan the start tag
//   for all namespace declarations.
//
// * The tag ends at EOF. Scan from beginning of tag to EOF for namespace
//   declarations.
//
// * The tag is terminated by an invalid token. Scan from beginning of tag to
//   error.
//
// Then issue the enterStartTag and attributeName events on the basis of what
// was found in scanning.
//
// When the parsing code discovers a change in namespace declarations, for
// instance because the user typed xmlns="..." or removed a declaration, the
// parsing code must *restart* validation *from* the location of the original
// enterStartTag event.
// A note on performance and the presence of the debug code here. We did a
// profiling test with the debug code entirely removed. It made no
// difference. Though TypeScript does not eliminate the code when DEBUG is
// false, its impact on real-world test runs is undetectable. (Note that
// uglification strips it from the minified code.)
const DEBUG = false;
// tslint:disable-next-line:strict-boolean-expressions
if (DEBUG) {
    //
    // Debugging utilities
    //
    const trace = (msg) => {
        console.log(msg); // tslint:disable-line:no-console
    };
    // @ts-ignore
    const stackTrace = () => {
        trace(new Error().stack);
    };
    // tslint:disable:no-var-keyword
    // @ts-ignore
    var possibleTracer;
    // @ts-ignore
    var fireEventTracer;
    // @ts-ignore
    var plainTracer;
    var callDump;
    // tslint:enable:no-var-keyword
    // tslint:disable-next-line:only-arrow-functions no-void-expression
    (function buildTracingCode() {
        let buf = "";
        const step = " ";
        const nameOrPath = (walker) => {
            const el = walker.el;
            if (el == null) {
                return "";
            }
            if (el.name === undefined) {
                return ` with path ${el.xmlPath}`;
            }
            const named = ` named ${el.name.toString()}`;
            if (walker.boundName == null) {
                return named;
            }
            return `${named} (bound to ${walker.boundName.toString()})`;
        };
        callDump = (msg, name, me) => {
            trace(`${buf}${msg}${name} on class ${me.constructor.name}` +
                ` id ${me.id}${nameOrPath(me)}`);
        };
        // tslint:disable-next-line:only-arrow-functions
        possibleTracer = function _possibleTracer(oldMethod, name, args) {
            buf += step;
            callDump("calling ", name, this);
            const ret = oldMethod.apply(this, args);
            callDump("called ", name, this);
            trace(`${buf}return from the call: ${util.inspect(ret)}`);
            buf = buf.slice(step.length);
            return ret;
        };
        // tslint:disable-next-line:only-arrow-functions
        fireEventTracer = function _fireEventTracer(oldMethod, name, args) {
            buf += step;
            callDump("calling ", name, this);
            trace(buf + util.inspect(args[0]));
            const ret = oldMethod.apply(this, args);
            callDump("called ", name, this);
            if (ret !== false) {
                trace(`${buf}return from the call: ${util.inspect(ret)}`);
            }
            buf = buf.slice(step.length);
            return ret;
        };
        // tslint:disable-next-line:only-arrow-functions
        plainTracer = function _plainTracer(oldMethod, name, args) {
            buf += step;
            callDump("calling ", name, this);
            const ret = oldMethod.apply(this, args);
            callDump("called ", name, this);
            trace(`${buf}return from the call: ${util.inspect(ret)}`);
            buf = buf.slice(step.length);
            return ret;
        };
    }());
    /**
     * Utility function for debugging. Wraps ``me[name]`` in a wrapper
     * function. ``me[name]`` must be a function.  ``me`` could be an instance or
     * could be a prototype. This function cannot trivially wrap the same field on
     * the same object twice.
     *
     * @private
     * @param me The object to modify.
     * @param name The field name to modify in the object.
     * @param f The function that should serve as wrapper.
     *
     */
    // @ts-ignore
    // tslint:disable-next-line:only-arrow-functions no-var-keyword prefer-const
    var wrap = (me, name, f) => {
        const mangledName = `___${name}`;
        me[mangledName] = me[name];
        // tslint:disable-next-line:only-arrow-functions
        me[name] = function wrapper() {
            return f.call(this, me[mangledName], name, arguments);
        };
    };
    /* tslint:enable */
}
class InternalFireEventResult {
    constructor(matched, errors, refs) {
        this.matched = matched;
        this.errors = errors;
        this.refs = refs;
    }
    static fromEndResult(result) {
        return (result === false) ?
            new InternalFireEventResult(true) :
            new InternalFireEventResult(false, result);
    }
    combine(other) {
        let errors;
        let refs;
        if (this.matched) {
            refs = this.refs;
            const oRefs = other.refs;
            if (oRefs !== undefined) {
                refs = refs === undefined ? oRefs : refs.concat(oRefs);
            }
        }
        else {
            errors = this.errors;
            const oErrors = other.errors;
            if (oErrors !== undefined) {
                errors = errors === undefined ? oErrors : errors.concat(oErrors);
            }
        }
        return new InternalFireEventResult(this.matched, errors, refs);
    }
}
exports.InternalFireEventResult = InternalFireEventResult;
/**
 * These patterns form a JavaScript representation of the simplified RNG
 * tree. The base class implements a leaf in the RNG tree. In other words, it
 * does not itself refer to children Patterns. (To put it in other words, it has
 * no subpatterns.)
 */
class BasePattern {
    /**
     * @param xmlPath This is a string which uniquely identifies the element from
     * the simplified RNG tree. Used in debugging.
     */
    constructor(xmlPath) {
        this.xmlPath = xmlPath;
    }
    /**
     * This method must be called after resolution has been performed.
     * ``_prepare`` recursively calls children but does not traverse ref-define
     * boundaries to avoid infinite regress...
     *
     * This function now performs these tasks:
     *
     * - it precomputes the values returned by ``hasAttr``,
     *
     * - it precomputes the values returned by ``hasEmptyPattern``,
     *
     * - it gathers all the namespaces seen in the schema.
     *
     * - it resolves the references.
     *
     * @param definitions The definitions present in the schema.
     *
     * @param namespaces An object whose keys are the namespaces seen in
     * the schema. This method populates the object.
     *
     * @returns The references that cannot be resolved, or ``undefined`` if no
     * references cannot be resolved. The caller is free to modify the value
     * returned as needed.
     *
     */
    _prepare(definitions, namespaces) {
        return undefined;
    }
    /**
     * This method tests whether a pattern is an attribute pattern or contains
     * attribute patterns. This method does not cross element boundaries. That is,
     * if element X cannot have attributes of its own but can contain elements
     * that can have attributes, the return value if this method is called on the
     * pattern contained by element X's pattern will be ``false``.
     *
     * @returns True if the pattern is or has attributes. False if not.
     */
    hasAttrs() {
        return false;
    }
    /**
     * This method determines whether a pattern has the ``empty``
     * pattern. Generally, this means that either this pattern is the ``empty``
     * pattern or has ``empty`` as a child.
     */
    hasEmptyPattern() {
        return false;
    }
}
exports.BasePattern = BasePattern;
/**
 * This is the common class from which patterns are derived. Most patterns
 * create a new walker by passing a name resolver. The one exception is
 * [[Grammar]], which creates the name resolver that are used by other
 * patterns. So when calling it we do not need a ``resolver`` parameter and thus
 * it inherits from [[BasePattern]] rather than [[Pattern]].
 */
class Pattern extends BasePattern {
    /**
     * Creates a new walker to walk this pattern.
     *
     * @returns A walker.
     */
    newWalker() {
        // Rather than make it abstract, we provide a default implementation for
        // this method, which throws an exception if called. We could probably
        // reorganize the code to do without but a) we would not gain much b) it
        // would complicate the type hierarchy. The cost is not worth the
        // benefits. There are two patterns that land on this default implementation
        // and neither can have newWalker called on them anyway.
        throw new Error("derived classes must override this");
    }
}
exports.Pattern = Pattern;
/**
 * Pattern objects of this class have exactly one child pattern.
 */
class OneSubpattern extends Pattern {
    constructor(xmlPath, pat) {
        super(xmlPath);
        this.pat = pat;
    }
    _prepare(definitions, namespaces) {
        const ret = this.pat._prepare(definitions, namespaces);
        this._cachedHasAttrs = this.pat.hasAttrs();
        this._cachedHasEmptyPattern = this._computeHasEmptyPattern();
        return ret;
    }
    hasAttrs() {
        // tslint:disable-next-line:no-non-null-assertion
        return this._cachedHasAttrs;
    }
    hasEmptyPattern() {
        // tslint:disable-next-line:no-non-null-assertion
        return this._cachedHasEmptyPattern;
    }
}
exports.OneSubpattern = OneSubpattern;
/**
 * Pattern objects of this class have exactly two child patterns.
 *
 */
class TwoSubpatterns extends Pattern {
    constructor(xmlPath, patA, patB) {
        super(xmlPath);
        this.patA = patA;
        this.patB = patB;
    }
    _prepare(definitions, namespaces) {
        const aRefs = this.patA._prepare(definitions, namespaces);
        const bRefs = this.patB._prepare(definitions, namespaces);
        this._cachedHasAttrs = this.patA.hasAttrs() || this.patB.hasAttrs();
        this._cachedHasEmptyPattern = this._computeHasEmptyPattern();
        if (aRefs !== undefined) {
            return bRefs === undefined ? aRefs : aRefs.concat(bRefs);
        }
        return bRefs;
    }
    hasAttrs() {
        // tslint:disable-next-line:no-non-null-assertion
        return this._cachedHasAttrs;
    }
    hasEmptyPattern() {
        // tslint:disable-next-line:no-non-null-assertion
        return this._cachedHasEmptyPattern;
    }
}
exports.TwoSubpatterns = TwoSubpatterns;
/**
 * This class models events occurring during parsing. Upon encountering the
 * start of a start tag, an "enterStartTag" event is generated, etc. Event
 * objects are held to be immutable. No precautions have been made to enforce
 * this. Users of these objects simply must not modify them.
 *
 * An event is made of a list of event parameters, with the first one being the
 * type of the event and the rest of the list varying depending on this type.
 */
class Event {
    /**
     * @param args... The event parameters may be passed directly in the call
     * ``(new Event(a, b, ...))`` or the first call parameter may be a list
     * containing all the event parameters ``(new Event([a, b, ])``. All of the
     * event parameters must be strings.
     */
    constructor(...args) {
        const params = (args.length === 1 && args[0] instanceof Array) ? args[0] : args;
        this.params = params;
        this.isAttributeEvent = (this.params[0] === "attributeName" ||
            this.params[0] === "attributeValue" ||
            this.params[0] === "attributeNameAndValue");
    }
    /**
     * @returns A string representation of the event.
     */
    toString() {
        return `Event: ${this.params.join(", ")}`;
    }
}
exports.Event = Event;
function isAttributeEvent(name) {
    // Using a set here is not clearly faster than using this logic.
    return (name === "attributeName" || name === "attributeValue" ||
        name === "attributeNameAndValue");
}
exports.isAttributeEvent = isAttributeEvent;
/**
 * Utility function used mainly in testing to transform a set of
 * events into a string containing a tree structure.  The principle is to
 * combine events of a same type together and among events of a same type
 * combine those which are in the same namespace. So for instance if there is a
 * set of events that are all attributeName events plus one ``leaveStartTag``
 * event, the output could be:
 *
 * <pre>``
 * attributeName:
 * ..uri A:
 * ....name 1
 * ....name 2
 * ..uri B:
 * ....name 3
 * ....name 4
 * leaveStartTag
 * ``</pre>
 *
 * The dots above are to represent more visually the indentation. Actual output
 * does not contain leading dots.  In this list there are two attributeName
 * events in the "uri A" namespace and two in the "uri B" namespace.
 *
 * @param evs Events to turn into a string.
 * @returns A string which contains the tree described above.
 */
function eventsToTreeString(evs) {
    const eventArray = (evs instanceof Set) ? Array.from(evs) : evs;
    const hash = new Map();
    eventArray.forEach((ev) => {
        const params = ev.params;
        let node = hash;
        const last = params.length - 1;
        for (let i = 0; i < params.length; ++i) {
            const key = params[i].toString();
            if (i === last) {
                node.set(key, false);
            }
            else {
                let nextNode = node.get(key);
                if (nextNode === undefined) {
                    nextNode = new Map();
                    node.set(key, nextNode);
                }
                node = nextNode;
            }
        }
    });
    function dumpTree(toDump, indent) {
        let ret = "";
        const keys = Array.from(toDump.keys());
        keys.sort();
        for (const key of keys) {
            // tslint:disable-next-line:no-non-null-assertion
            const sub = toDump.get(key);
            if (sub !== false) {
                ret += `${indent}${key}:\n`;
                ret += dumpTree(sub, `${indent}    `);
            }
            else {
                ret += `${indent}${key}\n`;
            }
        }
        return ret;
    }
    return dumpTree(hash, "");
    /* tslint:enable */
}
exports.eventsToTreeString = eventsToTreeString;
//  LocalWords:  RNG MPL lookahead xmlns uri CodeMirror tokenizer enterStartTag
//  LocalWords:  EOF attributeName el xmlPath buf nameOrPath util ret EventSet
//  LocalWords:  NameResolver args unshift HashSet subpatterns newID NG vm pre
//  LocalWords:  firstName lastName attributeValue leaveStartTag dumpTree const
//  LocalWords:  dumpTreeBuf subwalker fireEvent suppressAttributes HashMap
//  LocalWords:  ValidationError RefWalker DefineWalker
//# sourceMappingURL=base.js.map